<script lang="ts">
import { isInstitutionType } from '@shared/composables/useAppContext';

export type KindergartenClearing = 'internal' | 'parents' | 'web';
export type ClubClearing = 'all_employees' | 'certain_employees' | 'all_employees_and_all_members' | 'all_employees_and_certain_members';
export type Clearing = KindergartenClearing | ClubClearing;

interface ClearingOption {
    value: Clearing
    label: string
}

const KINDERGARTEN_CLEARING_OPTIONS: ClearingOption[] = [
    { value: 'internal', label: 'Intern und nur für Mitarbeiter und Kollegen der Einrichtung sichtbar.' },
    { value: 'parents', label: 'Für eingeloggte Eltern freigeben' },
    { value: 'web', label: 'Webfreigabe' },
];

const CLUB_CLEARING_OPTIONS: ClearingOption[] = [
    { value: 'all_employees', label: 'Intern und für alle Mitarbeiter des Vereins sichtbar' },
    { value: 'certain_employees', label: 'Intern und nur für ausgewählte Mitarbeiter sichtbar' },
    { value: 'all_employees_and_all_members', label: 'Für alle Mitarbeiter, alle Mitglieder und Gäste freigeben' },
    { value: 'all_employees_and_certain_members', label: 'Für ausgewählte Mitarbeiter und ausgewählte Mitglieder freigeben' },
];

// Institution-aware Optionsliste (Kita vs. Verein) wie Angular `AppContextService.isInstitutionType('club')`,
// optional gefiltert nach den vom Nutzer verwaltbaren Freigaben (`clearingOptionsAllowedToManage`-Input).
// Angular filters strictly (`allowedToManage.includes(...)`, clearing-select-control.component.ts)
// and has no fallback: a user who may manage nothing is offered nothing. Both employee transformers
// always send the list — full when the user may create news/events, empty otherwise — so falling
// back to the complete list would offer clearings the backend rejects.
export function getClearingOptions(allowedToManage?: string[] | null): ClearingOption[] {
    const all = isInstitutionType('club') ? CLUB_CLEARING_OPTIONS : KINDERGARTEN_CLEARING_OPTIONS;
    return all.filter((o) => (allowedToManage ?? []).includes(o.value));
}
</script>

<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps<{
    modelValue?: Clearing | null
    allowedToManage?: string[] | null
}>();
defineEmits<{ 'update:modelValue': [value: Clearing] }>();

const options = computed(() => getClearingOptions(props.allowedToManage));
</script>

<template>
    <select
        class="kgr-select"
        :value="modelValue"
        @change="$emit('update:modelValue', ($event.target as HTMLSelectElement).value as Clearing)"
    >
        <option
            v-for="opt in options"
            :key="opt.value"
            :value="opt.value"
        >
            {{ $t(opt.label) }}
        </option>
    </select>
</template>
